import type { Except } from "type-fest";
import { z } from "zod";
import {
    isoAndUnixZodSchema,
    nowIsoAndUnix,
    type IsoAndUnixTimestamp,
} from "../date-time/iso-and-unix";
import type { StorageItemImage } from "../storage-items/storage-item-images/storage-item-image";
import {
    isStorageItemImageId,
    type StorageItemImageId,
} from "../storage-items/storage-item-images/storage-item-image-id";
import {
    backgroundStyleZodSchema,
    type BackgroundStyle,
} from "../stylable/background-style";
import { tagsFactory, tagsZodSchema, type Tags } from "../tags";
import {
    requiredTranslatableZodSchema,
    translatableValueZodObject,
    translatableZodSchema,
    type RequiredTranslatable,
    type Translatable,
    type Translated,
} from "../translations/translation";
import type { MetaImage } from "./meta-image";
import { metaImageZodSchema } from "./meta-image";

type MetaContent = {
    readonly _type: "META_CONTENT";
    readonly _createdAt: IsoAndUnixTimestamp;
    /** Exempel: "REFRESHING • STICKY • SWEET" */
    readonly kicker: Translatable;
    readonly title: RequiredTranslatable;
    readonly subtitle: Translatable;
    readonly description: Translatable;
    /** Uppmaningstext (call to action text), ex: Boka, Köp, Pinga, Läs mer, Knapra */
    readonly actionText?: Translatable; // is there a better name for this
    readonly images: MetaImage[];
    readonly background?: BackgroundStyle | null;
    readonly tags: Tags;
};
const metaContentZodSchema: z.ZodSchema<MetaContent> = z.object({
    _type: z.literal("META_CONTENT"),
    _createdAt: isoAndUnixZodSchema,
    /** Exempel: "REFRESHING • STICKY • SWEET" */
    kicker: translatableZodSchema,
    title: requiredTranslatableZodSchema,
    subtitle: translatableZodSchema,
    description: translatableZodSchema,
    /** Uppmaningstext (call to action text), ex: Boka, Köp, Pinga, Läs mer, Knapra */
    actionText: translatableValueZodObject.nullish(),
    images: z.array(metaImageZodSchema),
    background: backgroundStyleZodSchema.nullish(),
    tags: tagsZodSchema,
});

const metaContentFactory = (
    params: Partial<Except<MetaContent, "title">> & {
        title: RequiredTranslatable;
    },
): MetaContent => {
    const defaultValues: Except<MetaContent, "title"> = {
        _type: "META_CONTENT",
        _createdAt: nowIsoAndUnix(),
        kicker: null,
        subtitle: null,
        description: null,
        actionText: null,
        images: [],
        background: null,
        tags: tagsFactory(),
    };
    return {
        ...defaultValues,
        ...params,
        title: params.title,
    };
};

type MetaContentTranslated = Translated<MetaContent>;

/**
 * Disclaimer, it might be an image, but it might not have
 * a storage item image id if it's the wrong type of
 * image reference. Just so you know :)
 */
const parseStorageItemImageId = (
    metaImage: MetaImage | StorageItemImage | undefined,
): StorageItemImageId | undefined => {
    if (typeof metaImage === "undefined") return undefined;
    return typeof metaImage === "string"
        ? undefined
        : "storageItemId" in metaImage
          ? isStorageItemImageId(metaImage.storageItemId)
              ? metaImage.storageItemId
              : undefined
          : undefined;
};

export { metaContentFactory, metaContentZodSchema, parseStorageItemImageId };
export type { MetaContent, MetaContentTranslated };
